import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Loader2, Building2, AlertTriangle, FileText } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function CompanyInfoTab({ onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    address: "",
    phone: "",
    email: "",
    abn: "",
    website: "",
    system_name: "",
    system_subtitle: "",
    notes: "",
    maintenance_mode: false,
    maintenance_message: "",
  });

  useEffect(() => {
    fetchCompanyInfo();
    axios.get(`${API}/api/terms?kind=login`).then((r) => setTermsContent(r.data?.content || "")).catch(() => {});
    axios.get(`${API}/api/terms?kind=card`).then((r) => setCardTermsContent(r.data?.content || "")).catch(() => {});
  }, []);

  const [termsContent, setTermsContent] = useState("");
  const [termsSaving, setTermsSaving] = useState(false);
  const [cardTermsContent, setCardTermsContent] = useState("");
  const [cardTermsSaving, setCardTermsSaving] = useState(false);

  const saveTerms = async () => {
    setTermsSaving(true);
    try {
      await axios.put(`${API}/api/terms`, { kind: "login", content: termsContent }, { headers: getAuthHeader() });
      toast.success("Login Terms & Conditions saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save terms");
    } finally {
      setTermsSaving(false);
    }
  };

  const saveCardTerms = async () => {
    setCardTermsSaving(true);
    try {
      await axios.put(`${API}/api/terms`, { kind: "card", content: cardTermsContent }, { headers: getAuthHeader() });
      toast.success("Payment card Terms & Conditions saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save terms");
    } finally {
      setCardTermsSaving(false);
    }
  };

  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);

  const refreshLogo = () => {
    setLogoUrl(`${API}/api/company-logo?t=${Date.now()}`);
  };
  useEffect(() => { refreshLogo(); }, []);

  const uploadLogo = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      setLogoBusy(true);
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const img = new Image();
          const url = URL.createObjectURL(f);
          img.onload = () => {
            const maxH = 200;
            const scale = Math.min(1, maxH / img.height);
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL("image/png"));
          };
          img.onerror = reject;
          img.src = url;
        });
        await axios.post(`${API}/api/company-logo`,
          { data: dataUrl.split(",")[1], content_type: "image/png" },
          { headers: getAuthHeader() });
        toast.success("Logo uploaded — it will appear in email headers");
        refreshLogo();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Logo upload failed");
      } finally {
        setLogoBusy(false);
      }
    };
    input.click();
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the company logo?")) return;
    try {
      await axios.delete(`${API}/api/company-logo`, { headers: getAuthHeader() });
      toast.success("Logo removed");
      setLogoUrl(null);
    } catch {
      toast.error("Failed to remove logo");
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/api/company-info`);
      setFormData(response.data);
    } catch (error) {
      console.error("Failed to load company info", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.address || !formData.phone || !formData.email || !formData.abn || !formData.system_name || !formData.system_subtitle) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/api/company-info`, formData, {
        headers: getAuthHeader(),
      });
      toast.success("Company information updated");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to update company information");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Building2 className="h-6 w-6" />
        <div>
          <h2 className="text-2xl font-bold">Company Information</h2>
          <p className="text-gray-500 text-sm">
            This information will appear on invoices and receipts
          </p>
        </div>
      </div>

      <Card className="p-6" data-testid="company-logo-card">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="min-w-0">
            <h3 className="font-semibold">Company logo</h3>
            <p className="text-sm text-gray-500">Shown in the header of customer & admin emails.</p>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Company logo"
                className="h-12 max-w-[180px] object-contain border rounded-md bg-neutral-50 px-2 py-1"
                onError={() => setLogoUrl(null)}
                data-testid="company-logo-preview"
              />
            )}
            <Button type="button" variant="outline" onClick={uploadLogo} disabled={logoBusy} data-testid="upload-logo-btn">
              {logoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (logoUrl ? "Replace logo" : "Upload logo")}
            </Button>
            {logoUrl && (
              <Button type="button" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={removeLogo} data-testid="remove-logo-btn">
                Remove
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="e.g., ABC Laundry Services Pty Ltd"
                required
              />
            </div>

            <div>
              <Label>System Name *</Label>
              <Input
                value={formData.system_name}
                onChange={(e) => setFormData({ ...formData, system_name: e.target.value })}
                placeholder="e.g., Laundry Manager"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Main title shown throughout the system
              </p>
            </div>

            <div>
              <Label>System Subtitle *</Label>
              <Input
                value={formData.system_subtitle}
                onChange={(e) => setFormData({ ...formData, system_subtitle: e.target.value })}
                placeholder="e.g., Hotel Linen System"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Subtitle displayed below the main title
              </p>
            </div>

            <div className="col-span-2">
              <Label>Address *</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street, Sydney NSW 2000, Australia"
                rows={3}
                required
              />
            </div>

            <div>
              <Label>Phone *</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+61 2 1234 5678"
                required
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contact@company.com"
                required
              />
            </div>

            <div>
              <Label>ABN (Australian Business Number) *</Label>
              <Input
                value={formData.abn}
                onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                placeholder="12 345 678 901"
                required
              />
            </div>

            <div>
              <Label>Website (Optional)</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://www.company.com"
              />
            </div>

            <div className="col-span-2">
              <Label>Notes (Bank Information, etc.)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Bank details, payment information, or any other notes..."
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">
                This information can be displayed on invoices or receipts
              </p>
            </div>
          </div>

          {/* ─── Maintenance mode ─────────────────────────────────────────── */}
          <div className="border-2 border-amber-300 bg-amber-50/50 rounded-lg p-4 space-y-3" data-testid="maintenance-section">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
                <div>
                  <Label className="text-base font-semibold text-amber-900">System Maintenance Mode</Label>
                  <p className="text-xs text-amber-800 mt-0.5">
                    When enabled, hotel customers will see a maintenance notice on the login page and cannot sign in.
                    Admin / Manager / Dispatcher accounts are unaffected.
                  </p>
                </div>
              </div>
              <Switch
                data-testid="maintenance-mode-toggle"
                checked={!!formData.maintenance_mode}
                onCheckedChange={(v) => setFormData({ ...formData, maintenance_mode: v })}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">
                Maintenance message (shown to customers)
              </Label>
              <Textarea
                data-testid="maintenance-message-input"
                value={formData.maintenance_message || ""}
                onChange={(e) => setFormData({ ...formData, maintenance_message: e.target.value })}
                placeholder="e.g. We're upgrading the system between 6 PM – 8 PM AEST. We'll be back shortly."
                rows={2}
                disabled={!formData.maintenance_mode}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Company Information"
              )}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-6" data-testid="terms-editor-card">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="h-5 w-5 text-brand-navy" />
          <h3 className="text-lg font-bold">Login Terms &amp; Conditions</h3>
        </div>
        <p className="text-gray-500 text-sm mb-4">
          Shown on the login page — users must tick the checkbox to accept before signing in.
        </p>
        <Textarea
          value={termsContent}
          onChange={(e) => setTermsContent(e.target.value)}
          rows={10}
          placeholder="Enter your terms and conditions here…"
          data-testid="terms-editor-textarea"
          className="text-sm"
        />
        <div className="flex justify-end pt-4">
          <Button onClick={saveTerms} disabled={termsSaving} data-testid="terms-editor-save">
            {termsSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Login T&C"
            )}
          </Button>
        </div>
      </Card>

      <Card className="p-6" data-testid="card-terms-editor-card">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="h-5 w-5 text-brand-navy" />
          <h3 className="text-lg font-bold">Payment Card Terms &amp; Conditions</h3>
        </div>
        <p className="text-gray-500 text-sm mb-4">
          Shown in the customer's payment-card dialog — customers must tick the box before adding or changing a card.
        </p>
        <Textarea
          value={cardTermsContent}
          onChange={(e) => setCardTermsContent(e.target.value)}
          rows={10}
          placeholder="Enter the payment card terms and conditions here…"
          data-testid="card-terms-editor-textarea"
          className="text-sm"
        />
        <div className="flex justify-end pt-4">
          <Button onClick={saveCardTerms} disabled={cardTermsSaving} data-testid="card-terms-editor-save">
            {cardTermsSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Card T&C"
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
